// BUILD-042 — Unified employee workspace, My HR (self). "History & Corrections" destination.
// Reads the caller's OWN durable correction requests (GET /hr/me/correction-requests via
// HrRequestsContext) — the same requests Personal Details / Job / Report-to submit and that HR
// Administration's Correction Requests queue reviews. Read-only here: deciding is an HR
// Administration action. A submitted request is the employee's receipt ("Awaiting HR review —
// no action needed from you"), not a to-do, so it is shown here but never added to the My HR
// action count.
import { useHrRequests } from '../HrRequestsContext';
import { CollapsibleHelp } from '../CollapsibleHelp';
import { CardTitle } from './SectionHeading';
import { AWAITING_HR_REVIEW_EMPLOYEE_LABEL } from './attentionModel';
import { formatLocalDateTime as formatRequestedAt } from '../hrFormat';

const STATUS_TONE: Record<string, string> = {
  open: 'bg-amber-50 text-amber-700 border-amber-200',
  resolved: 'bg-green-50 text-green-700 border-green-200',
  rejected: 'bg-gray-100 text-gray-500 border-gray-200',
};
const STATUS_LABEL: Record<string, string> = {
  open: 'Awaiting HR review', resolved: 'Resolved', rejected: 'Rejected',
};

export function HistorySelfSection({ employeeId }: { employeeId: string }) {
  const { myRequests } = useHrRequests();
  const mine = myRequests.filter((r) => r.employeeId === employeeId);

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
        <CardTitle>My correction requests</CardTitle>
        {mine.length === 0 ? (
          <div className="text-sm text-gray-400 mt-2">
            No correction requests yet. Submitting one from a governed field (Personal Details, Job, or Report-to) will appear here while HR reviews it.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {mine.map((r) => (
              <li key={r.requestId} className="py-3" data-testid="my-correction-row" data-status={r.status} data-field={r.fieldKey}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="text-sm">
                    <span className="font-medium text-gray-900">{r.fieldLabel}</span>
                    <span className="text-gray-500">: {r.currentValue ?? '—'} → {r.proposedValue}</span>
                  </div>
                  <span className={`text-[11px] border rounded-full px-2 py-0.5 flex-shrink-0 ${STATUS_TONE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                </div>
                <div className="text-xs text-gray-400 mt-1">Requested {formatRequestedAt(r.requestedAt)}</div>
                {r.status === 'open' && (
                  <div className="text-xs text-gray-500 mt-1" data-testid="my-correction-awaiting">{AWAITING_HR_REVIEW_EMPLOYEE_LABEL}.</div>
                )}
                {r.status === 'resolved' && r.decisionNote && (
                  <div className="text-xs text-green-700 mt-1" data-testid="my-correction-outcome">HR resolved this: {r.decisionNote}</div>
                )}
                {r.status === 'rejected' && r.decisionNote && (
                  <div className="text-xs text-gray-500 mt-1" data-testid="my-correction-outcome">HR did not make this change: {r.decisionNote}</div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <CollapsibleHelp>
        <p>This shows correction requests you have submitted on governed fields. A submitted request waits for HR review — no action is needed from you, and it does not count against your own To-dos. See Employment &amp; Pay &gt; Employment history for your actual recorded employment changes.</p>
      </CollapsibleHelp>
    </div>
  );
}
